/**
 * The pipeline board. Columns come from the stage vocabulary 017 pins with a CHECK; a move asks
 * for a reason.
 *
 * **The funnel does not move backwards, and the refusal is shown.** `applyStage` ranks the stages
 * and never writes an earlier one, so `POST /leads/:id/stage` answers 409 for a backwards drag.
 * The board keeps the card where the server says it is and prints the API's sentence in the
 * drawer — a card that snaps back with no explanation is how somebody concludes drag-and-drop is
 * flaky rather than that the rule exists.
 *
 * Author: Danish Khan
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api.ts';
import { useOpenLead } from '../lib/openLead.ts';
import { canWrite, roleGate } from '../lib/pii.ts';
import { useUser } from '../lib/session.tsx';
import { Button } from '../components/Button.tsx';
import { Drawer } from '../components/Drawer.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { Kanban } from '../components/Kanban.tsx';
import { useToast } from '../components/Toast.tsx';

export function PipelinePage(): ReactNode {
  const user = useUser();
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ leadId: string; stage: string; name: string } | null>(null);
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);

  const mayMove = canWrite(user.role);
  const moveGate = roleGate(mayMove, 'Moving a lead needs a role of rep or above', user.role);
  const openLead = useOpenLead((err) => toastError('open that lead', err));

  const pipeline = useQuery({ queryKey: ['pipeline'], queryFn: () => api.pipeline() });

  const move = useMutation({
    mutationFn: (vars: { leadId: string; stage: string; reason: string }) =>
      api.setStage(vars.leadId, vars.stage, vars.reason),
    onSuccess: () => {
      toast('Stage changed', { tone: 'ok', body: 'The reason is on the lead timeline.' });
      setPending(null);
      setReason('');
      setRefusal(null);
      void queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      void queryClient.invalidateQueries({ queryKey: ['lead'] });
    },
    onError: (err) => {
      // 409 is the funnel refusing a backwards move. It is not a failure to retry — it is the
      // rule — so it stays on screen in the drawer instead of flashing past in a toast.
      if (err instanceof ApiError && err.status === 409) {
        setRefusal(err.message);
        return;
      }
      toastError('move that lead', err);
    },
  });

  if (pipeline.isError) {
    return <ErrorState error={pipeline.error} context="load the pipeline" onRetry={() => void pipeline.refetch()} />;
  }

  const stages = pipeline.data?.stages ?? [];
  const cards = pipeline.data?.cards ?? [];

  /*
    There is no deal-value total here, and there is not going to be one until something collects a
    deal value. `routes/pipeline.ts` hard-codes `valueInr: null` on every card — "there is no
    deal-value field in this product: nothing collects one" — so the header read, permanently,
    "N open leads · no budget stated on any of them yet". That sentence blames the client's team
    for not recording budgets the product never asks for, on the first board a sales manager opens.
  */
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 border-b border-line bg-surface px-4 py-2.5">
        <p className="text-xs text-fg-muted">
          {cards.length} open lead{cards.length === 1 ? '' : 's'}
        </p>
        <p className="ml-auto text-[11px] text-fg-subtle">
          {moveGate ?? 'Drag a card, or use the “Move to” select on it — both ask for a reason. The funnel only moves forward.'}
        </p>
      </div>
      <div className="min-h-0 flex-1 p-3">
        {pipeline.isLoading ? (
          <p className="p-4 text-sm text-fg-muted">Loading pipeline…</p>
        ) : (
          <Kanban
            stages={stages}
            cards={cards}
            onOpen={(leadId) => openLead(leadId)}
            onMove={(leadId, stage) => {
              const card = cards.find((c) => c.leadId === leadId);
              if (!card || card.stage === stage) return;
              if (!mayMove) {
                toast('Cannot move that lead', { tone: 'danger', body: moveGate ?? '' });
                return;
              }
              setRefusal(null);
              setPending({ leadId, stage, name: card.displayName });
            }}
          />
        )}
      </div>

      <Drawer
        open={Boolean(pending)}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
            setReason('');
            setRefusal(null);
          }
        }}
        title="Why is this moving?"
        description={pending ? `${pending.name} → ${stages.find((s) => s.key === pending.stage)?.label ?? pending.stage}` : ''}
      >
        <div className="flex flex-col gap-3">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            autoFocus
            placeholder="e.g. quote sent for 80 pieces, waiting on the owner's sign-off"
            aria-label="Reason for the stage change"
            className="rounded-[var(--h1-radius)] border border-line-strong bg-bg px-3 py-2 text-sm text-fg"
          />
          <Button
            disabled={!reason.trim() || move.isPending}
            onClick={() => pending && move.mutate({ ...pending, reason: reason.trim() })}
          >
            {move.isPending ? 'Moving…' : 'Move lead'}
          </Button>
          {refusal && (
            <div role="alert" className="rounded-[var(--h1-radius)] border border-danger bg-danger-soft px-3 py-2">
              <p className="text-xs font-semibold text-danger-soft-fg">The funnel refused this move</p>
              <p className="mt-1 text-xs text-danger-soft-fg">{refusal}</p>
              <p className="mt-1 text-[11px] text-danger-soft-fg opacity-80">
                The card has not moved. Nothing was written — there is no half-applied stage change.
              </p>
            </div>
          )}
          <p className="text-[11px] text-fg-subtle">
            A stage that changes with no reason is a number nobody can explain at the end of the month.
          </p>
        </div>
      </Drawer>
    </div>
  );
}
